import { Proposal, ProposalStatus } from './proposal';
import { isArchivedRow } from '../utils/archived';

// Remaining-to-bill on a WON proposal (item 98): Approved = full total still to invoice;
// ApprovedDeposited = the final 50% (the deposit is already invoiced → counted in AR, so
// no double-count). Pending / Complete / Declined and voided proposals contribute nothing.
// item 760 (Chad) — the DEAL amount, RNWY-aware (mirrors the proposals grid + project bar):
// an engine proposal's retailProposalTotal is a placeholder, the engine amount is the deal.
// item 1146 (Chad: Bally's final 50% missing from the pipeline) — ONE deal-amount for the
// grid, the pipeline, and the finalize guards. Candidates, best wins:
//   1) the manual-pricing snapshot's best option (item 967 — priced-externally deals)
//   2) the engine perpetual rollup when it's a real number (item 760)
//   3) the INVOICE-DERIVED full total: proposal-split invoices carry FULL line copies with a
//      signed Discount, so net − discount = the materialized deal — the ground truth for any
//      engine deal that has billed anything (this is what Bally's was missing: its perpetual
//      rollup is 0.00 because it's a five-year sale).
// Fallback: the materialized line total (standard proposals).
export const proposalDealAmount = (p: Proposal, invoiceDerived = 0): number => {
  const isRnwy = (p as any).isRnwy || (p as any).kind === 1 || (p as any).kind === 2;
  let best = invoiceDerived > 0 ? invoiceDerived : 0;
  if (isRnwy) {
    const manualJson = (p as any).rnwyManualPricingJson;
    if (manualJson) {
      try {
        const m = JSON.parse(manualJson);
        const extras = (k: string) => (m.extraOneTime ?? [])
          .reduce((t: number, r: any) => t + (Number(r?.[k]) || 0), 0);
        const sp = m.switchPilot?.offered ? (Number(m.switchPilot?.oneTimeConfig) || 0) : 0;
        const sum = (o: any, k: string) => (o
          ? (Number(o.config) || 0) + (Number(o.graphics) || 0) + (Number(o.dev) || 0)
            + (Number(o.licenseOneTime) || 0) + (Number(o.retainBuyIn) || 0) + extras(k) + sp
          : 0);
        best = Math.max(best, sum(m.perpetual, 'perpetual'), sum(m.twoYear, 'twoYear'), sum(m.fiveYear, 'fiveYear'));
      } catch { /* fall through */ }
    }
    best = Math.max(best, (p as any).rnwyPerpetualAmount || 0);
  }
  if (best > 0) return best;
  return p.retailProposalTotal ?? (p as any).retailTotal ?? 0;
};

// item 1146 — the invoice-derived deal per proposal: the LARGEST full-line-copy total among
// its live invoices (net retailTotal minus the signed split Discount restores the full copy)
export const proposalInvoiceDealMap = (invoices: {
  proposalId?: string | null; isVoid?: boolean; retailTotal?: number; discount?: number | null;
}[]): Map<string, number> => {
  const m = new Map<string, number>();
  invoices.forEach((i) => {
    if (!i.proposalId || i.isVoid) return;
    const full = (i.retailTotal ?? 0) - (i.discount ?? 0); // discount is stored negative
    if (full > (m.get(i.proposalId) ?? 0)) m.set(i.proposalId, full);
  });
  return m;
};

// item 1137 (Chad) — ACTUALLY billed per proposal: the sum of live FINALIZED invoices tied
// to it (drafts don't count — they haven't billed; finalized invoices lock, so this only
// moves at finalize/void). One map feeds the pipeline remainder, the "billed $X of $Y"
// readouts, and the 1138/1139 finalize guards — they can never disagree.
type BilledInvoiceShape = {
  proposalId?: string | null;
  isVoid?: boolean;
  isFinalized?: boolean;
  status?: number;
  retailTotal?: number;
};

// InvoiceStatus.WrittenOff = 6 (kept numeric here — models/invoice imports models/pipeline)
const WRITTEN_OFF = 6;

// item 1221 (Chad: "look at a credit memo applied as final math — 500 deposit + 350 final
// + 150 credit = 1000, close") — an applied customer credit is money the deal ALREADY
// collected (the overpayment), so it counts as BILLED: pass the per-invoice applied-credit
// map and the billed total adds it back on top of the (net) invoice totals. Without it a
// credited final invoice would leave a phantom sliver in pipeline and never trip the
// fully-billed close prompt.
export const proposalBilledMap = (
  invoices: BilledInvoiceShape[],
  appliedCreditByInvoice?: Map<string, number>,
): Map<string, number> => {
  const m = new Map<string, number>();
  invoices.forEach((i) => {
    if (!i.proposalId || i.isVoid || !i.isFinalized || i.status === WRITTEN_OFF) return;
    const credit = appliedCreditByInvoice?.get((i as any).id) ?? 0;
    m.set(i.proposalId, (m.get(i.proposalId) ?? 0) + (i.retailTotal ?? 0) + credit);
  });
  return m;
};

// item 1221 — fold the credit-memo applications list into a per-invoice sum for the map above
export const appliedCreditMap = (
  applications: { invoiceId: string; amount: number }[],
): Map<string, number> => {
  const m = new Map<string, number>();
  applications.forEach((a) => m.set(a.invoiceId, (m.get(a.invoiceId) ?? 0) + (a.amount ?? 0)));
  return m;
};

export const proposalRemainingToBill = (
  p: Proposal, billed?: Map<string, number>, dealFromInvoices?: Map<string, number>,
): number => {
  if ((p as any).isVoid || isArchivedRow(p)) return 0; // item 714 — both archive fields
  // item 760 (Chad) — the KPI pipeline MUST count the unbilled remainder on RNWY deals too;
  // the raw retailProposalTotal under-counted engine proposals.
  const total = proposalDealAmount(p, dealFromInvoices?.get(p.id) ?? 0);
  if (p.status !== ProposalStatus.Approved && p.status !== ProposalStatus.ApprovedDeposited) return 0;
  // item 1137 — with the billed map, the remainder is the TRUTH (deal − actually billed):
  // survives edited deposits, custom splits, and manual overrides. Without it, fall back
  // to the old stage formula (Approved = all, Deposited = half).
  if (billed) return Math.max(0, total - (billed.get(p.id) ?? 0));
  return p.status === ProposalStatus.Approved ? total : total / 2;
};

export const hasRemainingToBill = (
  p: Proposal, billed?: Map<string, number>, dealFromInvoices?: Map<string, number>,
): boolean => proposalRemainingToBill(p, billed, dealFromInvoices) > 0;
